import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PinReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!/^\d{4}$/.test(pin)) { setError("PIN must be exactly 4 digits"); return; }
    if (pin !== confirm) { setError("PINs do not match"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/pin/reset`, { token, new_pin: pin });
      setDone(true);
      toast.success("PIN reset successfully");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not reset PIN");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-border/50" data-testid="pin-reset-card">
        <CardHeader>
          <CardTitle className="font-mono flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary" />
            Reset your Settings PIN
          </CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-emerald-400" />
                </div>
              </div>
              <p className="font-mono text-sm text-card-foreground">Your PIN has been updated.</p>
              <p className="font-mono text-xs text-muted-foreground">You can now open Settings with the new PIN.</p>
              <Button
                onClick={() => navigate("/settings")}
                className="btn-primary w-full"
                data-testid="pin-reset-goto-settings"
              >
                Go to Settings
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-xs font-mono text-muted-foreground">
                Choose a new 4-digit PIN. This will replace your previous PIN immediately.
              </p>
              <div className="space-y-2">
                <label className="font-mono text-xs">New 4-digit PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  autoFocus
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                  className="w-full text-center text-2xl font-mono tracking-[1em] rounded-lg border-2 border-border bg-card focus:border-primary outline-none py-3"
                  data-testid="pin-reset-new"
                />
              </div>
              <div className="space-y-2">
                <label className="font-mono text-xs">Confirm new PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                  className="w-full text-center text-2xl font-mono tracking-[1em] rounded-lg border-2 border-border bg-card focus:border-primary outline-none py-3"
                  data-testid="pin-reset-confirm"
                />
              </div>
              {error && (
                <p className="text-center text-sm text-destructive font-mono" data-testid="pin-reset-error">{error}</p>
              )}
              <Button
                type="submit"
                disabled={submitting || pin.length !== 4 || confirm.length !== 4}
                className="w-full btn-primary"
                data-testid="pin-reset-submit"
              >
                {submitting ? "Saving..." : "Save new PIN"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back to app
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
